"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMediaQuery } from "@mantine/hooks";

import { STICKY_HEADER_HEIGHT } from "@/app/(marketing)/constants";
import { hasMotion } from "@/app/lib/motion/MotionScript";

import {
  PLAY_PHASE,
  PlayEntry,
  PlayPhase,
  STACKED_SCROLL_CLEARANCE,
  STAGE_PIN_QUERY,
  StageView,
} from "./constants";
import { INITIAL_RULE, RuleState } from "./rule";
import { PLAY_DURATION_MS } from "./timeline";

/** A view's one play: whether it has run out, and how the view came on. */
interface ViewPlay {
  isDone: boolean;
  entry?: PlayEntry;
}

/** Every play still running, run out. */
function finishPlays(
  plays: Partial<Record<StageView, ViewPlay>>,
): Partial<Record<StageView, ViewPlay>> {
  const finished: Partial<Record<StageView, ViewPlay>> = {};

  for (const [view, viewPlay] of Object.entries(plays) as [StageView, ViewPlay][]) {
    finished[view] = { ...viewPlay, isDone: true };
  }

  return finished;
}

interface StageState {
  /** The views sit in one panel pinned beside the captions. False on the
      server and until the client measures, so the stacked layout is also the
      server's and the no-script reader's. */
  isPinned: boolean;
  /** The step whose caption last crossed the viewport's middle. */
  activeStep: number;
  setActiveStep: (step: number) => void;
  /** How far a view's one-time play has got. */
  phaseOf: (view: StageView) => PlayPhase;
  entryOf: (view: StageView) => PlayEntry | undefined;
  /** Plays a view the first time it shows; later calls do nothing. */
  play: (view: StageView, entry?: PlayEntry) => void;
  /** The rule in the review drawer, kept here so a layout flip keeps it. */
  rule: RuleState;
  setRule: (rule: RuleState) => void;
  /** Each step's node, registered by the layout, so a view can send the
      reader to another step without looking it up in the document. */
  registerStep: (view: StageView, node: HTMLDivElement | null) => void;
  stepNode: (view: StageView) => HTMLDivElement | null;
  /** Brings a step to the reader: pinned, its caption to the viewport's
      middle, which switches the stage; stacked, its caption just under the
      sticky header, so its view follows. */
  scrollToStep: (view: StageView) => void;
}

const StageContext = createContext<StageState | null>(null);

export function useStage(): StageState {
  const state = useContext(StageContext);

  if (!state) {
    throw new Error("useStage must be used inside a StageProvider");
  }

  return state;
}

/**
 * Everything the stage knows, held above its two layouts. Crossing the pin
 * threshold moves each view between the pinned panel and its own caption,
 * which remounts it; state kept here survives the move, so a view neither
 * replays nor loses what the reader did in it.
 */
export default function StageProvider({ children }: { children: React.ReactNode }) {
  const isPinned = useMediaQuery(STAGE_PIN_QUERY);
  const [activeStep, setActiveStep] = useState(0);
  const [plays, setPlays] = useState<Partial<Record<StageView, ViewPlay>>>({});
  const [playsLayout, setPlaysLayout] = useState(isPinned);
  const [rule, setRule] = useState<RuleState>(INITIAL_RULE);
  const startedViews = useRef(new Set<StageView>());
  const timers = useRef<number[]>([]);
  const stepNodes = useRef(new Map<StageView, HTMLDivElement>());

  // A layout flip remounts every view, and a CSS timeline would restart on
  // the new node, so any play still running shows its final state instead.
  // Adjusted during render, so no frame of the new layout replays.
  if (playsLayout !== isPinned) {
    setPlaysLayout(isPinned);
    setPlays(finishPlays);
  }

  useEffect(() => {
    const pending = timers.current;

    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const play = useCallback((view: StageView, entry?: PlayEntry) => {
    if (startedViews.current.has(view) || !hasMotion()) return;

    startedViews.current.add(view);

    const duration = PLAY_DURATION_MS[view];
    setPlays((previous) => ({
      ...previous,
      [view]: { isDone: duration === 0, entry },
    }));

    if (duration === 0) return;

    const timer = window.setTimeout(() => {
      setPlays((previous) => {
        const current = previous[view];

        return current ? { ...previous, [view]: { ...current, isDone: true } } : previous;
      });
    }, duration);

    timers.current.push(timer);
  }, []);

  const phaseOf = useCallback(
    (view: StageView): PlayPhase => {
      const viewPlay = plays[view];

      if (!viewPlay) return PLAY_PHASE.IDLE;

      return viewPlay.isDone ? PLAY_PHASE.DONE : PLAY_PHASE.PLAYING;
    },
    [plays],
  );

  const entryOf = useCallback((view: StageView) => plays[view]?.entry, [plays]);

  const registerStep = useCallback((view: StageView, node: HTMLDivElement | null) => {
    if (node) {
      stepNodes.current.set(view, node);
    } else {
      stepNodes.current.delete(view);
    }
  }, []);

  const stepNode = useCallback((view: StageView) => stepNodes.current.get(view) ?? null, []);

  const scrollToStep = useCallback(
    (view: StageView) => {
      const node = stepNodes.current.get(view);

      if (!node) return;

      const box = node.getBoundingClientRect();
      const top = isPinned
        ? box.top + box.height / 2 - window.innerHeight / 2
        : box.top - STICKY_HEADER_HEIGHT - STACKED_SCROLL_CLEARANCE;

      window.scrollBy({ top, behavior: hasMotion() ? "smooth" : "instant" });
    },
    [isPinned],
  );

  const state = useMemo(
    () => ({
      isPinned,
      activeStep,
      setActiveStep,
      phaseOf,
      entryOf,
      play,
      rule,
      setRule,
      registerStep,
      stepNode,
      scrollToStep,
    }),
    [isPinned, activeStep, phaseOf, entryOf, play, rule, registerStep, stepNode, scrollToStep],
  );

  return <StageContext.Provider value={state}>{children}</StageContext.Provider>;
}
